/**
 * Palettes, shapes and logo marks the website can be built with.
 *
 * These are the only design choices the admin exposes, and that is deliberate.
 * A free colour picker on a business website reliably produces unreadable text
 * on a coloured band; a short list of palettes that have been checked for
 * contrast cannot. Same reasoning for corner radius and logo: a few good
 * options beat infinite mediocre ones.
 *
 * Everything here is plain data with no imports, so the site can pull it in
 * through its own export path without dragging zod into the page build.
 */

export interface Palette {
  slug: string;
  label: string;
  /** One line on why it suits the business, shown under the swatch. */
  note: string;
  /** Overrides for the tokens in apps/site/src/styles/global.css. */
  tokens: {
    brand: string;
    brandDeep: string;
    accent: string;
    accentDeep: string;
  };
}

/**
 * Each palette pairs a dark brand colour with a brighter accent.
 *
 * The dark one carries the header, the footer and the credentials band, so it
 * has to hold white text at small sizes -- every one of these is well past the
 * 4.5:1 that needs. The accent is only ever used on buttons, links and small
 * marks, never as a background for body copy.
 */
export const palettes: readonly Palette[] = [
  {
    slug: "harbour",
    label: "Harbour",
    note: "The current look. Deep navy with a teal accent — reads clean and clinical.",
    tokens: {
      brand: "#0d3b5c",
      brandDeep: "#082639",
      accent: "#0f9b8e",
      accentDeep: "#0b7a70",
    },
  },
  {
    slug: "pine",
    label: "Pine",
    note: "Forest green. The most 'Muskoka' of these, and the least like a city cleaning franchise.",
    tokens: {
      brand: "#1d4732",
      brandDeep: "#12301f",
      accent: "#4f9d3a",
      accentDeep: "#3c7a2c",
    },
  },
  {
    slug: "slate",
    label: "Slate",
    note: "Near-black with a warm amber accent. Reads industrial and serious.",
    tokens: {
      brand: "#26303a",
      brandDeep: "#161d24",
      accent: "#d08334",
      accentDeep: "#a86526",
    },
  },
  {
    slug: "granite",
    label: "Granite",
    note: "Charcoal and cool blue. The most neutral — lets photos of the work carry the page.",
    tokens: {
      brand: "#333b45",
      brandDeep: "#1f252c",
      accent: "#3f7fb5",
      accentDeep: "#31648f",
    },
  },
  {
    slug: "cranberry",
    label: "Cranberry",
    note: "Deep red. Warmest of the set, and the one most likely to be remembered.",
    tokens: {
      brand: "#6b2233",
      brandDeep: "#4a1723",
      accent: "#c2603f",
      accentDeep: "#9c4a2f",
    },
  },
] as const;

export interface Shape {
  slug: string;
  label: string;
  note: string;
  tokens: { radius: string; radiusLg: string };
}

/**
 * How rounded everything is.
 *
 * A small lever with a large effect: it changes buttons, cards, photo corners
 * and the logo tile all at once, which is most of what makes a site feel
 * "modern" or "solid" without touching a single colour.
 */
export const shapes: readonly Shape[] = [
  {
    slug: "soft",
    label: "Soft",
    note: "Gently rounded. The current look.",
    tokens: { radius: "10px", radiusLg: "16px" },
  },
  {
    slug: "sharp",
    label: "Sharp",
    note: "Square corners. Reads more industrial and more formal.",
    tokens: { radius: "2px", radiusLg: "3px" },
  },
  {
    slug: "round",
    label: "Round",
    note: "Fully rounded buttons and cards. Friendlier, more residential.",
    tokens: { radius: "16px", radiusLg: "28px" },
  },
] as const;

export interface Logo {
  slug: string;
  label: string;
  note: string;
  /**
   * The mark only -- the business name is set as ordinary text beside it.
   *
   * Drawn on a 32x32 grid with no colours of its own: the main shape uses
   * `currentColor` and the highlight uses `var(--accent)`, so a mark follows
   * whichever palette is chosen instead of fighting it. Keeping the name out of
   * the SVG also means it stays selectable text, readable to screen readers and
   * to Google.
   */
  svg: string;
}

/**
 * Marks to choose from, drawn for this business rather than picked off a shelf.
 *
 * All six are simple geometry, because a logo is seen at 28px in a header and
 * on the side of a van far more often than it is seen large. Anything with fine
 * detail turns to mush at that size.
 */
export const logos: readonly Logo[] = [
  {
    slug: "pine",
    label: "Pine",
    note: "Highway 11 in one shape. Says where you are before it says what you do.",
    svg: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M16 3 22 13H19l4 7h-4l3 6H10l3-6H9l4-7h-3L16 3Z" fill="currentColor"/><rect x="14.6" y="24" width="2.8" height="5" rx="1" fill="var(--accent)"/></svg>`,
  },
  {
    slug: "droplet",
    label: "Droplet",
    note: "The plainest reading of clean. Works at any size and never needs explaining.",
    svg: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M16 3c0 0-9 10.2-9 15a9 9 0 0 0 18 0c0-4.8-9-15-9-15Z" fill="currentColor"/><path d="M16 23a5 5 0 0 1-5-5" stroke="var(--accent)" stroke-width="2.4" stroke-linecap="round"/></svg>`,
  },
  {
    slug: "roofline",
    label: "Roofline",
    note: "A building and a shine. The only one that reads for both cleaning and property management.",
    svg: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M4 15 16 5l12 10v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V15Z" fill="currentColor"/><path d="m21.5 17.5 1 2.6 2.6 1-2.6 1-1 2.6-1-2.6-2.6-1 2.6-1 1-2.6Z" fill="var(--accent)"/></svg>`,
  },
  {
    slug: "shine",
    label: "Shine",
    note: "A four-point sparkle. The most obviously 'cleaning' of the set.",
    svg: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M16 2c0 7 5 12 12 12-7 0-12 5-12 12 0-7-5-12-12-12 7 0 12-5 12-12Z" fill="currentColor"/><circle cx="26" cy="6" r="2.5" fill="var(--accent)"/></svg>`,
  },
  {
    slug: "lakeside",
    label: "Lakeside",
    note: "A roofline over water. Quietly local, and unusual for a trades business.",
    svg: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M5 16 16 6l11 10" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 22c2.4 0 2.4 2 4.8 2s2.4-2 4.8-2 2.4 2 4.8 2 2.4-2 4.8-2 2.4 2 3.8 2" stroke="var(--accent)" stroke-width="2.4" stroke-linecap="round"/><path d="M5 27.5c2.4 0 2.4 1.6 4.8 1.6" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" opacity=".35"/></svg>`,
  },
  {
    slug: "monogram",
    label: "Monogram",
    note: "Initials in a tile. The safest choice — it dates the slowest and prints the cleanest.",
    svg: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect width="32" height="32" rx="7" fill="currentColor"/><path d="M9 9v14M9 16h7M16 9v14" stroke="#fff" stroke-width="2.6" stroke-linecap="round"/><path d="M23.5 12.2a4.6 4.6 0 1 0 0 7.6" stroke="var(--accent)" stroke-width="2.6" stroke-linecap="round"/></svg>`,
  },
] as const;

export const DEFAULT_THEME = {
  palette: "harbour",
  shape: "soft",
  logo: "monogram",
} as const;

export function paletteBySlug(slug: string | undefined): Palette {
  return palettes.find((p) => p.slug === slug) ?? palettes[0]!;
}

export function shapeBySlug(slug: string | undefined): Shape {
  return shapes.find((s) => s.slug === slug) ?? shapes[0]!;
}

export function logoBySlug(slug: string | undefined): Logo {
  return logos.find((l) => l.slug === slug) ?? logos[logos.length - 1]!;
}

/**
 * The chosen theme as a CSS custom-property block.
 *
 * Emitted into the page rather than written into global.css so the stylesheet
 * stays the default and the override is visibly an override -- and so a build
 * with no published theme produces no block at all.
 */
export function themeCss(theme: { palette?: string; shape?: string } | undefined): string {
  const palette = paletteBySlug(theme?.palette);
  const shape = shapeBySlug(theme?.shape);

  return [
    ":root{",
    `--brand:${palette.tokens.brand};`,
    `--brand-deep:${palette.tokens.brandDeep};`,
    `--accent:${palette.tokens.accent};`,
    `--accent-deep:${palette.tokens.accentDeep};`,
    `--radius:${shape.tokens.radius};`,
    `--radius-lg:${shape.tokens.radiusLg};`,
    "}",
  ].join("");
}
